import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChatImageDropZone,
  useChatImages,
} from "@/core/components/chat-images";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { useBreadcrumbs } from "@/app-shell";
import {
  PROFITABILITY_PATH,
  PROFITABILITY_NEW_TEMPLATE_PATH,
  profitabilityTemplateEditPath,
} from "@/profitability/routes";
import { reportError } from "@/profitability/utils";
import {
  AGENT_IMAGE_HINT,
  TemplateChatComposer,
} from "./template-chat-composer";
import { useStartTemplateChat } from "./template-chat-query";

export const TEMPLATE_CHAT_STARTER_DESCRIPTION =
  "Describe the report you need and attach example files. Volca will help you build a reusable agent.";

// The composer that opens a template conversation. The Job Costing index
// leads with it, and the New template page holds it alone.
export const TemplateChatStart = ({ businessId }: { businessId: string }) => {
  const navigate = useNavigate();
  const start = useStartTemplateChat(businessId);
  const imageState = useState<File[]>([]);
  const chatImages = useChatImages(imageState, {
    refusedHint: AGENT_IMAGE_HINT,
  });
  return (
    <Stack gap={6} className="mx-auto w-full max-w-3xl">
      {/* Creating the agent leaves this page: a screenshot added meanwhile would be lost. */}
      <ChatImageDropZone
        onFiles={chatImages.addFiles}
        disabled={start.isPending}
      >
        <TemplateChatComposer
          spacious
          businessId={businessId}
          imageState={imageState}
          imagesLocked={start.isPending}
          disabled={start.isPending}
          placeholder="Describe your report…"
          onSend={(message, documentIds, images) =>
            new Promise<boolean>((resolve) =>
              start.mutate(
                { message, document_ids: documentIds, images },
                {
                  onSuccess: ({ report }) => {
                    resolve(true);
                    navigate(profitabilityTemplateEditPath(report.id));
                  },
                  onError: (error) => {
                    toast.error("The conversation could not start", {
                      description: `${reportError(error)} Try sending your description again.`,
                    });
                    resolve(false);
                  },
                },
              ),
            )
          }
        />
      </ChatImageDropZone>
      <Button
        variant="link"
        className="self-start"
        onClick={() => navigate(`${PROFITABILITY_NEW_TEMPLATE_PATH}/form`)}
      >
        Fill in a form instead
      </Button>
    </Stack>
  );
};

export const TemplateChatStarter = ({ businessId }: { businessId: string }) => {
  useBreadcrumbs([
    { label: "Job Costing", href: PROFITABILITY_PATH },
    { label: "New agent" },
  ]);
  return (
    <PageContainer>
      <PageHeader
        title="New agent"
        description={TEMPLATE_CHAT_STARTER_DESCRIPTION}
      />
      <TemplateChatStart businessId={businessId} />
    </PageContainer>
  );
};
